import React from 'react';
import moment from 'moment';
import { DatePicker } from 'antd';
import { FilePdfOutlined, PictureOutlined, PlusOutlined, MinusOutlined, CloseOutlined } from '@ant-design/icons';
import './key-findings-cumulative.scss';
import { LOADER_RED } from '../../utils/constant';
import LS_SERVICE from '../../utils/localStorage';

// Keys inside a parameter object that are metadata, not dates
const META_KEYS = ['UOM', 'reference_range'];

// Column widths (px). Keep in sync with $kf-name-w / $kf-range-w / $kf-trend-w
// in key-findings-cumulative.scss, which also handles sticky positioning.
const NAME_COL_W = 250;
const DATE_COL_MIN_W = 96;
const RANGE_COL_W = 96;
const TREND_COL_W = 80;

// Formats the API date key as "Oct 5, 2026 | 11:10 AM"; falls back to the raw split
const DATE_FORMATS = ['DD MMM YYYY hh:mm A', 'DD MMM, YYYY hh:mm A', 'D MMM YYYY h:mm A', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601];
const formatDateKey = (date) => {
  const m = moment(String(date), DATE_FORMATS, true);
  if (m.isValid()) return { day: m.format('MMM D, YYYY'), time: m.format('h:mm A') };
  const parts = String(date).split(' ');
  return { day: parts.slice(0, 3).join(' '), time: parts.slice(3).join(' ') };
};

const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';

// From/To picker display, e.g. "07-OCT-2026" (moment's MMM gives "Oct", so uppercase it)
const formatPickerDate = (value) => (value ? value.format('DD-MMM-YYYY').toUpperCase() : '');

// Only allow http(s) or relative links from the API (blocks javascript: etc.)
const safeUrl = (url) => {
  if (!hasValue(url)) return null;
  const s = String(url).trim();
  return /^(https?:)?\/\//i.test(s) || s.startsWith('/') ? s : null;
};

// ALL-CAPS section headings ending in a colon, e.g. "FINDINGS:", "IMPRESSION:"
const IMPRESSION_HEADING_RE = /\b[A-Z][A-Z0-9\s/&\-]{2,}:/g;

/**
 * Turns plain impression text into React nodes with bold section headings.
 * Each heading starts on a new line (except the first thing in the text).
 * Returns the text unchanged when no headings are found. No HTML is injected.
 */
export const formatImpression = (text) => {
  const source = String(text ?? '');
  const re = new RegExp(IMPRESSION_HEADING_RE.source, 'g');
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = re.exec(source)) !== null) {
    let before = source.slice(lastIndex, match.index);
    if (nodes.length === 0 && before.trim() === '') {
      before = ''; // heading is the first thing in the text: no line break
    } else {
      before = before.replace(/[ \t]+$/, '');
      if (!before.endsWith('\n')) before += '\n'; // avoid doubling an existing break
    }
    if (before) nodes.push(before);
    nodes.push(
      <strong key={`h-${match.index}`} className="kf-report-heading">{match[0]}</strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (!nodes.length) return source;
  nodes.push(source.slice(lastIndex));
  return nodes;
};

const isOnHold = (flag) => flag === true || flag === 1 || ['true', '1', 'y', 'yes'].includes(String(flag ?? '').toLowerCase());

class KeyFindingsCumulative extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
      expanded: {},
    };
  }

  getData = () => this.props.data ?? {};

  handleFromChange = (date) => {
    if (!date || !this.props.onDateRangeChange) return;
    this.props.onDateRangeChange(date.clone().startOf('day'), this.props.toDate);
  };

  handleToChange = (date) => {
    if (!date || !this.props.onDateRangeChange) return;
    this.props.onDateRangeChange(this.props.fromDate, date.clone().endOf('day'));
  };

  disabledFromDate = (current) => current && (current > moment().endOf('day') || (this.props.toDate && current > this.props.toDate));

  disabledToDate = (current) => current && (current > moment().endOf('day') || (this.props.fromDate && current < moment(this.props.fromDate).startOf('day')));

  isAdmittingDoctor = () => {
    const { admittingDoctorId } = this.props;
    const staffId = LS_SERVICE.get('staff_id');
    return hasValue(admittingDoctorId) && hasValue(staffId) && String(admittingDoctorId) === String(staffId);
  };

  getStatusClass = (status) => {
    if (status === 'N') return 'text-success'; // normal
    if (status === 'P') return 'text-danger'; // critical
    if (status === 'A' || status === 'L' || status === 'H') return 'text-warning'; // abnormal / high / low
    return '';
  };

  // Orders date keys using the API's kfdates sequence; unknown dates go last
  sortDates = (dates) => {
    const order = this.getData()?.kfdates ?? [];
    return [...dates].sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
    });
  };

  getDateKeys = (obj) => Object.keys(obj || {}).filter((k) => !META_KEYS.includes(k));

  // Builds the three card groups, dropping parameters and dates with no data
  buildCards = () => {
    const data = this.getData();
    const groupData = data?.kfDisplayData ?? {};
    const invData = data?.kfDisplayDataInv ?? {};
    const reportData = data?.kfDisplayDataNonNumeric ?? {};

    const grouped = [];
    Object.keys(groupData).forEach((group) => {
      const params = [];
      const dateSet = new Set();
      Object.keys(groupData[group] || {}).forEach((param) => {
        const paramObj = groupData[group][param] || {};
        const dates = this.getDateKeys(paramObj).filter((d) => hasValue(paramObj[d]?.test_value));
        if (dates.length) {
          dates.forEach((d) => dateSet.add(d));
          params.push({ name: param, values: paramObj, UOM: paramObj.UOM, reference_range: paramObj.reference_range });
        }
      });
      if (params.length) {
        grouped.push({ id: `group|${group}`, name: group, params, dates: this.sortDates([...dateSet]) });
      }
    });

    const standalone = [];
    Object.keys(invData).forEach((test) => {
      const testObj = invData[test] || {};
      const dates = this.getDateKeys(testObj).filter((d) => hasValue(testObj[d]?.test_value));
      if (dates.length) {
        standalone.push({
          id: `inv|${test}`,
          name: test,
          params: [{ name: test, values: testObj, UOM: testObj.UOM, reference_range: testObj.reference_range }],
          dates: this.sortDates(dates),
        });
      }
    });

    const reports = [];
    Object.keys(reportData).forEach((report) => {
      const reportObj = reportData[report] || {};
      const dates = this.getDateKeys(reportObj).filter((d) => {
        const r = reportObj[d];
        // A report date needs an image or PDF link to be shown
        return hasValue(r?.imageURL) || hasValue(r?.result_pdf);
      });
      if (dates.length) {
        reports.push({ id: `report|${report}`, name: report, values: reportObj, dates: this.sortDates(dates) });
      }
    });

    return { grouped, standalone, reports };
  };

  cardMatches = (card, query) => {
    if (!query) return true;
    if (card.name.toLowerCase().includes(query)) return true;
    return (card.params || []).some((p) => p.name.toLowerCase().includes(query));
  };

  handleSearchChange = (e) => {
    const value = e.target.value;
    const query = value.trim().toLowerCase();
    if (!query) {
      this.setState({ searchQuery: value });
      return;
    }
    // Expand every card that matches the search
    const { grouped, standalone, reports } = this.buildCards();
    const expanded = { ...this.state.expanded };
    [...grouped, ...standalone, ...reports].forEach((card) => {
      if (this.cardMatches(card, query)) expanded[card.id] = true;
    });
    this.setState({ searchQuery: value, expanded });
  };

  clearSearch = () => {
    this.setState({ searchQuery: '' });
  };

  toggleCard = (id) => {
    this.setState((prev) => ({ expanded: { ...prev.expanded, [id]: !prev.expanded[id] } }));
  };

  openTrend = (e, payload) => {
    if (this.props.handleModalPopupStateLift) {
      this.props.handleModalPopupStateLift(e, 'keyFindingChartModalNew', payload);
    }
  };

  renderDateHeader = (date) => {
    const { day, time } = formatDateKey(date);
    return (
      <th key={date} className="kf-col-date">
        {day}{time ? ` | ${time}` : ''}
      </th>
    );
  };

  // Value is coloured by status; UOM stays neutral. Abnormal/critical values are bold.
  renderValue = (cell, UOM) => {
    if (!cell || !hasValue(cell.test_value)) return '-';
    if (isOnHold(cell.confidential_report_release_hold) && !this.isAdmittingDoctor()) {
      return <span className="text-secondary">Confidential</span>;
    }
    const status = cell.isNormal;
    const emphasis = ['P', 'A', 'L', 'H'].includes(status);
    const isHtml = /<[^>]+>/.test(cell.test_value);
    return (
      <span className={`kf-value ${emphasis ? 'kf-value-flagged' : ''}`}>
        <span className={this.getStatusClass(status)}>
          {isHtml ? <span dangerouslySetInnerHTML={{ __html: cell.test_value }} /> : cell.test_value}
        </span>
        {UOM ? <span className="kf-uom">{UOM}</span> : null}
      </span>
    );
  };

  renderReferenceRange = (range, UOM) => {
    if (!hasValue(range)) return '-';
    return (
      <>
        <div dangerouslySetInnerHTML={{ __html: range }} />
        {hasValue(UOM) ? <div>( {UOM} )</div> : null}
      </>
    );
  };

  renderTrendButton = (label, payload) => (
    <button
      type="button"
      className="btn chart-btn button-chart-individual p-0"
      aria-label={`Trend graph for ${label}`}
      title="Trend graph"
      onClick={(e) => this.openTrend(e, payload)}
    >
      <i style={{ width: '20px', height: '20px' }} className="icon-chart"></i>
    </button>
  );

  // trendPayload: chart data for the header trend icon; null hides the icon (e.g. reports)
  renderCardShell = (card, body, trendPayload = null) => {
    const isOpen = !!this.state.expanded[card.id];
    const bodyId = `kf-cum-${card.id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    return (
      <div key={card.id} className={`kf-cum-card ${isOpen ? 'open' : ''}`}>
        <div className="kf-cum-card-header" onClick={() => this.toggleCard(card.id)}>
          <h6 className="kf-cum-card-title">{card.name}</h6>
          {trendPayload ? (
            <button
              type="button"
              className="kf-cum-icon-btn"
              aria-label={`Trend graph for ${card.name}`}
              title="Trend graph"
              onClick={(e) => { e.stopPropagation(); this.openTrend(e, trendPayload); }}
            >
              <i className="icon-chart"></i>
            </button>
          ) : null}
          <button
            type="button"
            className="kf-cum-icon-btn"
            aria-expanded={isOpen}
            aria-controls={bodyId}
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${card.name}`}
            onClick={(e) => { e.stopPropagation(); this.toggleCard(card.id); }}
          >
            {isOpen ? <MinusOutlined /> : <PlusOutlined />}
          </button>
        </div>
        {isOpen ? (
          <div id={bodyId} className="kf-cum-card-body">
            {body}
          </div>
        ) : null}
      </div>
    );
  };

  renderLabCard = (card, isGroup) => {
    const { chartDataGroup = {} } = this.getData() || {};
    const headerTrend = isGroup
      ? { type: 'finding-group', name: 'Key Findings Trend Graphs', testData: chartDataGroup[card.name], testName: card.name }
      : {
          type: 'individual', id: 1, name: 'Key Findings Trend Graphs',
          isNormal: card.dates.map((d) => card.params[0]?.values[d]?.isNormal).find(hasValue),
          testDataIndividual: { [card.name]: chartDataGroup[card.name] },
          testName: card.name,
        };
    const body = (
      <table
        className="kf-cum-table"
        // Fill the card, but never squeeze date columns below DATE_COL_MIN_W;
        // beyond that the table scrolls sideways
        style={{ width: '100%', minWidth: `${NAME_COL_W + card.dates.length * DATE_COL_MIN_W + RANGE_COL_W + TREND_COL_W}px` }}
      >
        <thead>
          <tr>
            <th className="kf-col-name">Parameter</th>
            {card.dates.map(this.renderDateHeader)}
            <th className="kf-col-range">Reference Range</th>
            <th className="kf-col-trend">Trend Graph</th>
          </tr>
        </thead>
        <tbody>
          {card.params.map((param) => {
            const latestStatus = card.dates.map((d) => param.values[d]?.isNormal).find(hasValue);
            const trendPayload = isGroup
              ? { type: 'individual', id: 1, name: 'Key Findings Trend Graphs', isNormal: latestStatus, testDataIndividual: chartDataGroup[card.name], testName: param.name }
              : { type: 'individual', id: 1, name: 'Key Findings Trend Graphs', isNormal: latestStatus, testDataIndividual: { [card.name]: chartDataGroup[card.name] }, testName: param.name };
            return (
              <tr key={param.name}>
                <td className="kf-col-name">{param.name}</td>
                {card.dates.map((d) => (
                  <td key={d} className="kf-col-date">{this.renderValue(param.values[d], param.UOM)}</td>
                ))}
                <td className="kf-col-range">{this.renderReferenceRange(param.reference_range, param.UOM)}</td>
                <td className="kf-col-trend">{this.renderTrendButton(param.name, trendPayload)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
    return this.renderCardShell(card, body, headerTrend);
  };

  renderReportLinks = (image, pdf) => (
    <>
      {image ? (
        <a href={image} target="_blank" rel="noopener noreferrer" className="kf-report-link" title="Open image" aria-label="Open image">
          <PictureOutlined />
        </a>
      ) : null}
      {pdf ? (
        <a href={pdf} target="_blank" rel="noopener noreferrer" className="kf-report-link" title="Open PDF" aria-label="Open PDF">
          <FilePdfOutlined />
        </a>
      ) : null}
    </>
  );

  // - links + impression: structured, scrollable text with a pinned action bar
  // - links only: compact cell (impression, if any, as tooltip)
  // - no links: "-"
  renderReportCell = (r = {}) => {
    const image = safeUrl(r.imageURL);
    const pdf = safeUrl(r.result_pdf);
    if (!image && !pdf) return <div className="kf-report-compact">-</div>;

    const impression = typeof r.impression === 'string' ? r.impression.trim() : '';
    if (impression) {
      return (
        <div className="kf-report-cell">
          <div className="kf-report-text">{formatImpression(impression)}</div>
          <div className="kf-report-actions">{this.renderReportLinks(image, pdf)}</div>
        </div>
      );
    }
    return (
      <div className="kf-report-compact" title={hasValue(r.impression) ? String(r.impression) : undefined}>
        {this.renderReportLinks(image, pdf)}
      </div>
    );
  };

  renderReportCard = (card) => {
    const count = card.dates.length;
    // 1–2 dates share the full width; more than 2 -> each takes half of
    // (visible width - 250px frozen name column) and the table scrolls.
    const tableStyle = count > 2
      ? { width: `calc(${NAME_COL_W}px + ${count} * ((100% - ${NAME_COL_W}px) / 2))` }
      : { width: '100%' };

    const body = (
      <table className="kf-cum-table kf-report-table" style={tableStyle}>
        <thead>
          <tr>
            <th className="kf-col-name">Report</th>
            {card.dates.map(this.renderDateHeader)}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="kf-col-name">{card.name}</td>
            {card.dates.map((d) => (
              <td key={d} className="kf-col-date kf-report-td">
                {this.renderReportCell(card.values[d])}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );
    return this.renderCardShell(card, body, null);
  };

  render() {
    const { searchQuery } = this.state;
    const { fromDate, toDate, loading } = this.props;
    const query = searchQuery.trim().toLowerCase();
    const cards = this.buildCards();
    const grouped = cards.grouped.filter((c) => this.cardMatches(c, query));
    const standalone = cards.standalone.filter((c) => this.cardMatches(c, query));
    const reports = cards.reports.filter((c) => this.cardMatches(c, query));
    const nothing = !grouped.length && !standalone.length && !reports.length;

    return (
      <div className="key-findings-cumulative">
        <div className="kf-cum-toolbar">
          {/* Same markup and width as the normal view's search box */}
          <div className="antInputItem">
            <div className="form-group mb-0 kf-search-wrap" style={{ width: '300px' }}>
              <input
                type="text"
                placeholder="Search..."
                aria-label="Search key findings"
                className="form-control"
                value={searchQuery}
                onChange={this.handleSearchChange}
              />
              {searchQuery ? (
                <button type="button" className="kf-search-clear" aria-label="Clear search" title="Clear search" onClick={this.clearSearch}>
                  <CloseOutlined />
                </button>
              ) : null}
            </div>
          </div>
          {/* ORDER DATE / RESULT DATE selector passed in by the parent */}
          {this.props.dateTypeSelector ? (
            <div className="kf-date-type">{this.props.dateTypeSelector}</div>
          ) : null}
          <div className="kf-cum-right">
            {this.props.onToggleViewMode ? (
              <div className="kf-view-toggle" role="group" aria-label="Key findings view">
                <button type="button" className="kf-view-btn" aria-pressed="false" onClick={this.props.onToggleViewMode}>
                  Normal View
                </button>
                <button type="button" className="kf-view-btn active" aria-pressed="true">
                  Cumulative View
                </button>
              </div>
            ) : null}
            <label htmlFor="kf-cum-from">From:</label>
            <DatePicker
              id="kf-cum-from"
              value={fromDate}
              format={formatPickerDate}
              allowClear={false}
              disabledDate={this.disabledFromDate}
              onChange={this.handleFromChange}
            />
            <label htmlFor="kf-cum-to">To:</label>
            <DatePicker
              id="kf-cum-to"
              value={toDate}
              format={formatPickerDate}
              allowClear={false}
              disabledDate={this.disabledToDate}
              onChange={this.handleToChange}
            />
          </div>
        </div>

        <div className="kf-cum-list custom-scrollbar">
          {loading ? (
            <div className="w-100 d-flex align-items-center justify-content-center py-5">{LOADER_RED}</div>
          ) : nothing ? (
            <div className="kf-cum-empty">No Record Found</div>
          ) : (
            <>
              {/* Order: grouped panels, standalone tests, then reports */}
              {grouped.map((c) => this.renderLabCard(c, true))}
              {standalone.map((c) => this.renderLabCard(c, false))}
              {reports.map(this.renderReportCard)}
            </>
          )}
        </div>
      </div>
    );
  }
}

export default KeyFindingsCumulative;
